/**
 * Gas Threshold Alerts Component
 * Displays alerts and highlights for high gas consumption periods
 */

import React, { useState, useEffect } from 'react';
import { analyticsService, GasAnalytics } from '../services/analytics.service';

interface GasThresholdAlertsProps {
  selectedCurrency: 'HBAR' | 'USD' | 'INR';
  thresholds?: {
    warning: number;
    critical: number;
  };
}

interface Alert {
  id: string;
  type: 'warning' | 'critical' | 'info';
  title: string;
  message: string;
  value: number;
  timestamp: Date;
}

export const GasThresholdAlerts: React.FC<GasThresholdAlertsProps> = ({
  selectedCurrency,
  thresholds = {
    warning: selectedCurrency === 'HBAR' ? 10 : selectedCurrency === 'USD' ? 0.5 : 42,
    critical: selectedCurrency === 'HBAR' ? 50 : selectedCurrency === 'USD' ? 2.5 : 210
  }
}) => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [, setGasAnalytics] = useState<GasAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGasAnalytics();
  }, [selectedCurrency]);

  const fetchGasAnalytics = async () => {
    try {
      setLoading(true);
      const data = await analyticsService.getGasAnalytics();
      setGasAnalytics(data);
      generateAlerts(data);
    } catch (error) {
      console.error('Error fetching gas analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCostValue = (item: any, currency: 'HBAR' | 'USD' | 'INR') => {
    switch (currency) {
      case 'HBAR':
        return item.totalCostHBAR || item.hbar || 0;
      case 'USD':
        return item.totalCostUSD || item.usd || 0;
      case 'INR':
        return item.totalCostINR || item.inr || 0;
      default:
        return 0;
    }
  };

  const formatCurrency = (value: number, currency: 'HBAR' | 'USD' | 'INR') => {
    switch (currency) {
      case 'HBAR':
        return `${value.toFixed(8)} HBAR`;
      case 'USD':
        return `$${value.toFixed(4)}`;
      case 'INR':
        return `₹${value.toFixed(2)}`;
      default:
        return value.toString();
    }
  };

  const generateAlerts = (data: GasAnalytics) => {
    const newAlerts: Alert[] = [];

    // Check total cost against thresholds
    const totalCost = getCostValue(data, selectedCurrency);
    if (totalCost >= thresholds.critical) {
      newAlerts.push({
        id: 'total-cost-critical',
        type: 'critical',
        title: 'Critical Gas Consumption',
        message: `Total gas cost has reached ${formatCurrency(totalCost, selectedCurrency)}, exceeding the critical threshold of ${formatCurrency(thresholds.critical, selectedCurrency)}.`,
        value: totalCost,
        timestamp: new Date()
      });
    } else if (totalCost >= thresholds.warning) {
      newAlerts.push({
        id: 'total-cost-warning',
        type: 'warning',
        title: 'High Gas Consumption',
        message: `Total gas cost is ${formatCurrency(totalCost, selectedCurrency)}, approaching the warning threshold.`,
        value: totalCost,
        timestamp: new Date()
      });
    }

    // Check for expensive operations
    const expensiveOperations = data.operationBreakdown
      .filter(op => getCostValue(op, selectedCurrency) >= thresholds.warning)
      .sort((a, b) => getCostValue(b, selectedCurrency) - getCostValue(a, selectedCurrency));

    expensiveOperations.forEach((op, index) => {
      const cost = getCostValue(op, selectedCurrency);
      newAlerts.push({
        id: `expensive-operation-${index}`,
        type: cost >= thresholds.critical ? 'critical' : 'warning',
        title: `Expensive Operation: ${op.operationType}`,
        message: `${op.operationType} operations have cost ${formatCurrency(cost, selectedCurrency)} with ${op.transactionCount} transactions.`,
        value: cost,
        timestamp: new Date()
      });
    });

    // Check for high-cost trends
    const recentTrends = data.costTrends.slice(-7); // Last 7 data points
    const highCostDays = recentTrends.filter(trend => 
      getCostValue(trend, selectedCurrency) >= thresholds.warning
    );

    if (highCostDays.length >= 3) {
      newAlerts.push({
        id: 'high-cost-trend',
        type: 'warning',
        title: 'Sustained High Gas Costs',
        message: `Gas costs have been consistently high over the past ${highCostDays.length} periods.`,
        value: highCostDays.reduce((sum, day) => sum + getCostValue(day, selectedCurrency), 0) / highCostDays.length,
        timestamp: new Date()
      });
    }

    // Check benchmark performance if available
    if (data.benchmarkComparison) {
      const efficiency = data.benchmarkComparison.costEfficiencyRating;
      if (efficiency === 'POOR' || efficiency === 'BELOW_AVERAGE') {
        newAlerts.push({
          id: 'poor-efficiency',
          type: efficiency === 'POOR' ? 'critical' : 'warning',
          title: `${efficiency === 'POOR' ? 'Poor' : 'Below Average'} Cost Efficiency`,
          message: `Your gas efficiency is ${efficiency.toLowerCase().replace('_', ' ')} compared to platform averages (${data.benchmarkComparison.percentileRanking.toFixed(1)}th percentile).`,
          value: data.benchmarkComparison.percentileRanking,
          timestamp: new Date()
        });
      }
    }

    setAlerts(newAlerts);
  };

  const getAlertIcon = (type: 'warning' | 'critical' | 'info') => {
    switch (type) {
      case 'critical':
        return '🚨';
      case 'warning':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      default:
        return '📊';
    }
  };

  const getAlertStyles = (type: 'warning' | 'critical' | 'info') => {
    switch (type) {
      case 'critical':
        return 'bg-red-50 border-red-200 text-red-800';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200 text-yellow-800';
      case 'info':
        return 'bg-blue-50 border-blue-200 text-blue-800';
      default:
        return 'bg-gray-50 border-gray-200 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <span className="ml-2">Checking thresholds...</span>
      </div>
    );
  }

  if (alerts.length === 0) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-md p-4">
        <div className="flex items-center">
          <span className="text-green-600 text-lg mr-2">✅</span>
          <div>
            <h3 className="text-sm font-medium text-green-800">All Clear</h3>
            <div className="mt-1 text-sm text-green-700">
              Gas consumption is within normal thresholds.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">Gas Consumption Alerts</h3>
        <div className="text-sm text-gray-500">
          Thresholds: Warning {formatCurrency(thresholds.warning, selectedCurrency)} | 
          Critical {formatCurrency(thresholds.critical, selectedCurrency)}
        </div>
      </div>

      {alerts.map((alert) => (
        <div
          key={alert.id}
          className={`border rounded-md p-4 ${getAlertStyles(alert.type)}`}
        >
          <div className="flex items-start">
            <span className="text-lg mr-3">{getAlertIcon(alert.type)}</span>
            <div className="flex-1">
              <h4 className="text-sm font-medium">{alert.title}</h4>
              <div className="mt-1 text-sm">{alert.message}</div>
              <div className="mt-2 text-xs opacity-75">
                {alert.timestamp.toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Threshold Configuration */}
      <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Threshold Settings</h4>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <label className="block text-gray-600">Warning Threshold</label>
            <div className="font-medium">{formatCurrency(thresholds.warning, selectedCurrency)}</div>
          </div>
          <div>
            <label className="block text-gray-600">Critical Threshold</label>
            <div className="font-medium">{formatCurrency(thresholds.critical, selectedCurrency)}</div>
          </div>
        </div>
        <div className="mt-2 text-xs text-gray-500">
          Thresholds automatically adjust based on selected currency
        </div>
      </div>
    </div>
  );
};

export default GasThresholdAlerts;